import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import { useGetSocialPlatformsQuery } from "slice/common";
import { useGetSocialLinksQuery, useSetSocialLinksMutation } from "slice/user";
import { SocialResponse } from "../../PersonalDetails/types";

export const useSocialMedia = () => {
    const navigateTo = useNavigate();
    const [socialHttpMethod, setSocialHttpMethod] = useState("POST");
    const [socialHandles, setSocialHandles] = useState<SocialResponse[]>([
        {
            id: String(new Date().getTime()),
            username: "",
            social_platforms_id: 1,
        },
    ]);

    const { data: socialPlatforms = [] } = useGetSocialPlatformsQuery({});
    const userId = useAppSelector((state) => state.user.id);

    const { data: socialHandleData, isFetching: isSocialFetching } =
        useGetSocialLinksQuery(userId ?? "", {
            refetchOnMountOrArgChange: true,
        });

    const [setSocialLinks, { isLoading, isSuccess, isError }] =
        useSetSocialLinksMutation();

    const addSocialHandle = useCallback(() => {
        setSocialHandles((prevState) => [
            ...prevState,
            {
                id: String(new Date().getTime()),
                username: "",
                social_platforms_id: 1,
            },
        ]);
    }, []);

    const handleSocialSelect = useCallback((value: number, id: string) => {
        setSocialHandles((prevState) =>
            prevState.map((prev) =>
                prev.id === id
                    ? {
                          ...prev,
                          social_platforms_id: value,
                      }
                    : prev
            )
        );
    }, []);

    const handleSocialUsername = useCallback((event: any, id: string) => {
        const { value } = event.target;
        setSocialHandles((prevState) => [
            ...prevState.map((prev) =>
                prev.id === id ? { ...prev, username: value } : prev
            ),
        ]);
    }, []);

    const getSocialPlatformOptions = useCallback(
        (socialPlatforms: Array<SocialResponse>) =>
            socialPlatforms.map((socialPlatform: any) => ({
                value: socialPlatform.id,
                label: socialPlatform.name,
            })),
        []
    );

    const removeSocialHandle = (id: string) => {
        addToast({
            type: "success",
            text: "Social Media link Deleted successfully",
        });
        setSocialHandles((prev) => prev.filter((i) => i.id !== id));
    };

    const handleSubmit = () => {
        setSocialLinks({
            socialHandles,
            method: socialHttpMethod,
            user_id: userId ?? "",
        });
    };

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Data saved successfully!",
            });
            navigateTo(routeConstants.agencyInformation);
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            addToast({
                type: "error",
                text: "Something went wrong, please try again.",
            });
        }
    }, [isError]);

    useEffect(() => {
        if (socialHandleData?.length) {
            setSocialHandles(socialHandleData);
            setSocialHttpMethod("PUT");
        }
    }, [socialHandleData, getSocialPlatformOptions]);

    return {
        socialHandles,
        addSocialHandle,
        removeSocialHandle,
        isFetchingData: isSocialFetching,
        isLoading,
        handleSubmit,
        handleSocialSelect,
        handleSocialUsername,
        socialPlatformsOptions: getSocialPlatformOptions(socialPlatforms),
    };
};
